import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import styled from "styled-components";

import Button from "components/atoms/Button";
import Icon from "components/atoms/Icon";
import Alert from "components/atoms/alert/Alert";
import FormInput from "components/atoms/input/FormInput";
import useLoading from "components/atoms/loading/useLoading";
import Text from "components/atoms/text/Text";
import useToast from "components/atoms/toast/useToast";

import AbstractModalFooter from "components/page/modal/AbstractModalFooter";

import {
    usePaymentBillingKeyCreate,
    usePaymentBillingKeyDelete,
    usePaymentPlan,
    useSpacePaymentHistoryCreate,
} from "hooks/queries/usePayment";

import Constants from "utils/constants";

import moment from "moment";

const Container = styled.div`
    width: 100%;
    max-height: 80vh;
    overflow-y: auto;
`;

const SubInfoBox = styled.div`
    margin-top: 40px;
    padding: 8px 16px;
    width: 100%;
    background-color: var(--color-InfoBox);
    align-items: center;
    border-radius: 4px;
`;

const InfoListWrap = styled.ul`
    margin: 0;
    padding: 0 0 0 16px;
`;

const SubInfoItem = styled.li`
    margin: 4px 0;
    line-height: 1.4;
    text-align: justify;
    color: var(--color-SubBlack);
`;

const Wrapper = styled.div`
    margin: 20px 0 0 0px;
`;

const OptionWrapper = styled.div`
    display: flex;
    flex-direction: column;
`;

const Option = styled.label`
    display: block;
    align-items: center;
    margin-bottom: 15px;
    padding: 16px;
    border-radius: 4px;
    cursor: pointer;
    border: ${(props) => (props.selected ? "1px solid var(--color-Button1)" : "1px solid #ccc")};

    &:last-child {
        margin-bottom: 0;
    }
`;

const HeaderRow = styled.div`
    display: flex;
    align-items: center;
    margin-bottom: 16px;
`;

const OptionTitleText = styled(Text)`
    font-size: 1.4rem;
    font-weight: 500;
    margin: 40px 0 20px 0;
    padding: 0 0 0 8px;
    border-left: solid 2px var(--color-TitleBar);
`;

const contentStyle = {
    variant: "body-md",
    color: "var(--color-Black)",
};

const listStyle = {
    variant: "body-sm",
    textAlign: "justify",
};

const PlanInfoArea = styled.div`
    text-align: center;
`;

const PlanInfoText = styled(Text)`
    margin-top: 12px;
    font-size: 1.4rem;
    font-weight: 400;
    color: var(--color-SubBlack);
`;

const PlanSubText = styled(Text)`
    font-size: 1.2rem;
    font-weight: 400;
    color: var(--color-SubBlack);
    margin-top: 12px;
`;

const PaymentMethodText = styled(Text)`
    font-size: 1.4rem;
    color: var(--color-Black);
`;

const CardOptionWrapper = styled(Option)`
    flex-direction: column;
    align-items: flex-start;
    padding: 16px;
`;

const CardInfoBox = styled(SubInfoBox)`
    margin-top: 0;
    padding: 16px;
`;

const InfoRow = styled.div`
    display: flex;
    justify-content: space-between;
    margin-bottom: 8px;

    &:last-child {
        margin-bottom: 0;
    }
`;

const MemberRow = styled.div`
    display: flex;
    align-items: center;
    margin-top: 12px;
`;

const MemberCountText = styled(Text)`
    font-size: 1.4rem;
    font-weight: 400;
    color: var(--color-SubBlack);
    margin-left: 8px;
`;

const Divider = styled.hr`
    border: none;
    border-top: 1px solid var(--color-Line);
    margin: 12px 0;
`;

const FeeSubText = styled(Text)`
    font-size: 1.2rem;
    font-weight: 400;
    color: var(--color-Grey1);
`;

function SpaceMemberAddModal(props) {
    const { space_uuid } = useParams();
    const [paymentMethod, setPaymentMethod] = useState("card");

    const [addedCount, setAddedCount] = useState(1);

    const paymentPlanQuery = usePaymentPlan();
    const [plan, setPlan] = useState({});

    const billingKeyMutation = usePaymentBillingKeyCreate();
    const billingKeyDelete = usePaymentBillingKeyDelete();
    const spacePaymentHistoryCreateMutation = useSpacePaymentHistoryCreate();

    const { setLoading } = useLoading();
    const { setToast } = useToast();

    useEffect(() => {
        if (paymentPlanQuery.data) {
            setPlan(paymentPlanQuery.data.find((item) => item.id === Constants.GROUP_PLAN) || {});
        }
    }, [paymentPlanQuery.data]);

    useEffect(() => {
        const jquery = document.createElement("script");
        jquery.src = "https://code.jquery.com/jquery-1.12.4.min.js";
        const iamport = document.createElement("script");
        iamport.src = "https://cdn.iamport.kr/js/iamport.payment-1.1.8.js";
        document.head.appendChild(jquery);
        document.head.appendChild(iamport);

        return () => {
            document.head.removeChild(jquery);
            document.head.removeChild(iamport);
        };
    }, []);

    const handlePaymentError = (error) => {
        if (error?.cancelled) {
            return;
        }
        if (error?.type === "billingKey") {
            Alert("warn", "에러", "결제 정보 등록 중 오류가 발생했습니다. 결제 정보를 다시 확인해주세요.");
            return;
        }
        if (error?.type === "subscription") {
            Alert("warn", "에러", "멤버 수 변경 처리 중 오류가 발생했습니다. 관리자에게 문의해주세요.");
            return;
        }
        if (error?.error_msg) {
            if (error.error_msg === "사용자가 결제를 취소하셨습니다") {
                return;
            }
            Alert("warn", "에러", "결제 중 오류가 발생했습니다. 결제 정보를 다시 확인해주세요.");
            return;
        }
        Alert("warn", "에러", error?.response?.data?.error || "요청 중 오류가 발생했습니다. 관리자에게 문의해주세요.");
    };

    const buildMerchantInfo = (email) => {
        const randomNumber = Math.floor(Math.random() * 90000) + 10000;
        const emailPrefix = (email || "user").split("@")[0];
        return {
            merchantUid: `member_add_${emailPrefix}_${randomNumber}`,
            customerUid: `${emailPrefix}_${randomNumber}`,
        };
    };

    const buildIamportPayload = ({ amount, merchantUid, customerUid }) => {
        const payload = {
            pg: `${process.env.REACT_APP_ENV !== "prd" ? "danal_tpay.9810030929" : "danal_tpay"}`,
            pay_method: "card",
            merchant_uid: merchantUid,
            name: `${plan.name || "Group"} 멤버 추가`,
            amount: amount,
            customer_uid: customerUid,
            buyer_email: props.user?.email,
        };

        if (props.lastPaymentHistory?.ended_at) {
            payload.period = {
                from: moment().format("YYYY-MM-DD"),
                to: moment(props.lastPaymentHistory.ended_at).format("YYYY-MM-DD"),
            };
        }

        return payload;
    };

    const requestIamportPayment = (payload) =>
        new Promise((resolve, reject) => {
            const { IMP } = window;
            IMP.init("imp65161342");
            document.getElementById("iamport-modal").style.display = "block";
            IMP.request_pay(payload, (response) => {
                document.getElementById("iamport-modal").style.display = "none";
                if (response.success) {
                    resolve(response);
                    return;
                }
                if (response.error_msg === "사용자가 결제를 취소하셨습니다") {
                    reject({ cancelled: true });
                    return;
                }
                reject(response);
            });
        });

    const payForMemberAdd = async () => {
        try {
            if (paymentAmount < 100) {
                Alert("warn", "에러", "100원 미만은 결제할 수 없습니다.");
                return;
            }

            const { merchantUid, customerUid } = buildMerchantInfo(props.user?.email);
            const payload = buildIamportPayload({
                amount: paymentAmount,
                merchantUid,
                customerUid,
            });

            const response = await requestIamportPayment(payload);

            setLoading(true, "결제 요청 중입니다.");
            let billingKey;
            try {
                billingKey = await billingKeyMutation.mutateAsync(response);
            } catch (error) {
                throw { type: "billingKey", original: error };
            }

            try {
                const cardPaymentPayload = {
                    started_at: moment().format("YYYY-MM-DD"),
                    ended_at: moment(props.lastPaymentHistory.ended_at).format("YYYY-MM-DD"),
                    plan: plan.id,
                    member_count: addedCount,
                    discount_policy: (props.lastPaymentHistory?.discount_policy || []).map((policy) => policy.id),
                    amount: paymentAmount,
                    status: Constants.PAYMENT_STATUS_COMPLETED,
                    description: `${plan.name} 요금제 ${addedCount}명 추가 결제`,
                    auto_extend: false,
                    payment_type: Constants.PAYMENT_TYPE_DIRECT_ADD,
                    card: billingKey.id,
                    iamport_id: merchantUid,
                    coupon: null,
                    is_extend: false,
                };

                await spacePaymentHistoryCreateMutation.mutateAsync({
                    space_uuid,
                    data: cardPaymentPayload,
                });
            } catch (error) {
                throw { type: "subscription", original: error };
            }

            billingKeyDelete.mutate(billingKey.id);

            setToast("멤버 수가 변경되었습니다.", "info");
            props.exitModal();
        } catch (error) {
            console.log("payment error", error);
            handlePaymentError(error);
        } finally {
            setLoading(false);
        }
    };

    const discountPolicies = props.lastPaymentHistory?.discount_policy || [];
    const longTermDiscountPolicy = discountPolicies.find(
        (policy) => policy.internal_code === Constants.THREE_MONTH_DISCOUNT_POLICY,
    );
    const groupDiscountPolicy = discountPolicies.find(
        (policy) => policy.internal_code === Constants.GROUP_DISCOUNT_POLICY,
    );
    const endedAtMoment = props.lastPaymentHistory?.ended_at
        ? moment(props.lastPaymentHistory.ended_at).startOf("day")
        : null;
    const todayMoment = moment().startOf("day");
    const remainingDays = endedAtMoment ? Math.max(0, endedAtMoment.diff(todayMoment, "days") + 1) : 0;

    const currentMaxMember = props.user?.current_space?.max_member_count || 0;
    const nextMaxMember = currentMaxMember + addedCount;
    const baseMonthlyAmount = plan?.price || 0;
    const appliedLongTermDiscountRate = Number(longTermDiscountPolicy?.discount_rate || 0);
    const appliedLongTermDiscountAmount = (baseMonthlyAmount * appliedLongTermDiscountRate) / 100;
    const appliedGroupDiscountRate = Number(groupDiscountPolicy?.discount_rate || 0);
    const appliedGroupDiscountAmount = (baseMonthlyAmount * appliedGroupDiscountRate) / 100;
    const discountedMonthlyAmount = baseMonthlyAmount - appliedLongTermDiscountAmount - appliedGroupDiscountAmount;
    const discountedDailyAmount = discountedMonthlyAmount / 30;
    const paymentAmount = Math.round(discountedDailyAmount * addedCount * remainingDays);
    const isPayDisabled =
        !props.lastPaymentHistory ||
        addedCount < 1 ||
        paymentAmount < 100 ||
        spacePaymentHistoryCreateMutation.isLoading;

    return (
        <Container>
            <PlanInfoArea>
                <PlanInfoText>현재 그룹의 최대 멤버 수를 추가할 수 있습니다.</PlanInfoText>
                <PlanSubText>요금은 남은 이용 기간을 계산하여 결제됩니다.</PlanSubText>
                {!props.lastPaymentHistory && (
                    <PlanSubText>최근 결제 이력이 없어 멤버 추가 결제를 진행할 수 없습니다.</PlanSubText>
                )}
            </PlanInfoArea>

            <OptionTitleText>추가 멤버</OptionTitleText>
            <Wrapper>
                <MemberRow>
                    <Button
                        onlyIcon
                        bgColor={"var(--color-Button4)"}
                        hoverBgColor={"var(--color-ButtonHover4)"}
                        margin={"0 8px 0 0"}
                        onClick={() => {
                            setAddedCount(Math.max(1, addedCount - 1));
                        }}
                    >
                        <Icon name={"minus"} size={"12"} color={"var(--color-Grey1)"} />
                    </Button>
                    <FormInput
                        width="60px"
                        height="32px"
                        labelDp="none"
                        type="number"
                        className="form-control"
                        name={"member_count"}
                        textAlign={"center"}
                        maxLength={255}
                        value={addedCount}
                        onChange={(e) => {
                            const value = parseInt(e.target.value, 10);
                            setAddedCount(Number.isNaN(value) ? 1 : value);
                        }}
                        onKeyDown={(e) => {
                            if (e.key === "-" || e.key === "Subtract") {
                                e.preventDefault();
                            }
                        }}
                        onBlur={(e) => {
                            const value = parseInt(e.target.value, 10);
                            if (Number.isNaN(value) || value < 1) {
                                setAddedCount(1);
                            } else {
                                setAddedCount(value);
                            }
                        }}
                    />
                    <Button
                        margin={"0 0 0 8px"}
                        onlyIcon
                        bgColor={"var(--color-Button4)"}
                        hoverBgColor={"var(--color-ButtonHover4)"}
                        onClick={() => {
                            setAddedCount(addedCount + 1);
                        }}
                    >
                        <Icon name={"plus"} size={"12"} color={"var(--color-Grey1)"} />
                    </Button>
                    <MemberCountText>(변경 후 최대 멤버 수: {nextMaxMember}명)</MemberCountText>
                </MemberRow>
            </Wrapper>

            <OptionTitleText>결제 옵션</OptionTitleText>
            <Wrapper>
                <OptionWrapper>
                    <CardOptionWrapper
                        selected={paymentMethod === "card"}
                        onClick={() => {
                            setPaymentMethod("card");
                        }}
                    >
                        <HeaderRow>
                            <PaymentMethodText>카드 결제</PaymentMethodText>
                        </HeaderRow>
                        {paymentMethod === "card" && (
                            <CardInfoBox>
                                <InfoRow>
                                    <Text {...contentStyle}>기본 요금 (1명, 1개월 기준)</Text>
                                    <Text {...contentStyle}>{plan.price?.toLocaleString()}원</Text>
                                </InfoRow>
                                <InfoRow style={{ paddingLeft: "8px" }}>
                                    <FeeSubText>
                                        장기 결제 할인 ({Number(longTermDiscountPolicy?.discount_rate || 0)}%)
                                    </FeeSubText>
                                    <FeeSubText>
                                        {appliedLongTermDiscountAmount
                                            ? `-${appliedLongTermDiscountAmount.toLocaleString()}원`
                                            : "해당없음"}
                                    </FeeSubText>
                                </InfoRow>
                                <InfoRow style={{ paddingLeft: "8px" }}>
                                    <FeeSubText>
                                        그룹 결제 할인 ({Number(groupDiscountPolicy?.discount_rate || 0)}%)
                                    </FeeSubText>
                                    <FeeSubText>
                                        {appliedGroupDiscountAmount
                                            ? `-${appliedGroupDiscountAmount.toLocaleString()}원`
                                            : "해당없음"}
                                    </FeeSubText>
                                </InfoRow>
                                <InfoRow>
                                    <Text {...contentStyle}>월 이용 요금</Text>
                                    <Text {...contentStyle}>
                                        {discountedMonthlyAmount
                                            ? `${discountedMonthlyAmount.toLocaleString()}원`
                                            : "-"}
                                    </Text>
                                </InfoRow>

                                <Divider />

                                <InfoRow>
                                    <Text {...contentStyle}>일할 기준 요금 (월 요금 / 30)</Text>
                                    <Text {...contentStyle}>
                                        {discountedDailyAmount ? `${discountedDailyAmount.toLocaleString()}원` : "-"}
                                    </Text>
                                </InfoRow>

                                <InfoRow>
                                    <Text {...contentStyle}>추가 멤버 수</Text>
                                    <Text {...contentStyle}>{addedCount} 명</Text>
                                </InfoRow>

                                <InfoRow>
                                    <Text {...contentStyle}>잔여 이용 기간</Text>
                                    <Text {...contentStyle}>{remainingDays}일</Text>
                                </InfoRow>

                                <Divider />

                                <InfoRow>
                                    <Text {...contentStyle} fontWeight="bold">
                                        결제 금액
                                    </Text>
                                    <Text {...contentStyle} fontWeight="bold">
                                        {paymentAmount > 0 ? `${paymentAmount.toLocaleString()}원` : "-"}
                                    </Text>
                                </InfoRow>
                            </CardInfoBox>
                        )}
                    </CardOptionWrapper>
                </OptionWrapper>
            </Wrapper>

            <SubInfoBox>
                <InfoListWrap>
                    <SubInfoItem>
                        <Text {...listStyle}>표시된 모든 금액은 부가세(VAT)가 포함된 금액입니다.</Text>
                    </SubInfoItem>
                    <SubInfoItem>
                        <Text {...listStyle}>결제 완료 후 환불은 제공되지 않습니다.</Text>
                    </SubInfoItem>
                </InfoListWrap>
            </SubInfoBox>

            <AbstractModalFooter
                leftBtnText={"취소"}
                rightBtnText={"결제하기"}
                leftOnClick={props.exitModal}
                rightOnClick={payForMemberAdd}
                disabled={isPayDisabled}
            />
            <div
                style={{
                    display: "none",
                    overFlow: "scroll",
                    position: "fixed",
                    zIndex: 10000,
                    top: 0,
                    left: 0,
                    width: "100vw",
                    height: "100vh",
                    padding: "120px 0",
                    background: "rgba(0,0,0,.48)",
                    boxSizing: "border-box",
                }}
                id={"iamport-modal"}
            ></div>
        </Container>
    );
}

export default SpaceMemberAddModal;
